// Direct imports for optimal tree shaking
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { TraceScene } from "../core/TraceScene";
import type { SceneObject } from "../core/SceneObject";
import type { BehaviorData } from "../scenedata/behaviordata";
import { Logger } from "./Logger";

export interface GLBModelInfo {
    defaultDimensions: { x: number, y: number, z: number };
    vertexCount: number;
    triangleCount: number;
    materialCount: number;
    fileSize: string;
    gltfExtensions: string[];
    boundingBox: { min: Vector3, max: Vector3 };
    meshCount: number;
}

export interface TraceFileStats {
    totalObjects: number;
    glbCount: number;
    particleSystemCount: number;
    imageCount: number;
    videoCount: number;
}

export type SceneInfoResult =
    | { fileType: 'glb'; data: GLBModelInfo | null }
    | { fileType: 'trace'; data: TraceFileStats };

/**
 * Handles model information extraction for the TraceViewer
 */
export class ModelInfo {
    private readonly traceScene: TraceScene;
    /** Cache for file size and other model info per object */
    private _infoCache: Map<string, { fileSize?: number; isEstimated?: boolean; gltfExtensions?: string[] }> = new Map();

    constructor(traceScene: TraceScene) {
        this.traceScene = traceScene;
    }

    /** Get detailed information about a GLB model */
    public getModelInfo(objectName: string): GLBModelInfo | null {
        const sceneObject = this.traceScene.resolveSceneObject(objectName);
        if (!sceneObject) {
            Logger.warn(`No scene object found for ${objectName}`);
            return null;
        }

        const root = sceneObject.transform;
        const meshes = this._getAllMeshes(root);
        
        if (meshes.length === 0) {
            Logger.warn(`No meshes found for object ${objectName}`);
            return null;
        }

        // Calculate bounding box
        const boundingBox = this._calculateBoundingBox(meshes);
        const dimensions = {
            x: boundingBox.max.x - boundingBox.min.x,
            y: boundingBox.max.y - boundingBox.min.y,
            z: boundingBox.max.z - boundingBox.min.z
        };

        // Count vertices and triangles
        let totalVertices = 0;
        let totalTriangles = 0;
        const materials = new Set<string>();
        
        meshes.forEach(mesh => {
            if (mesh.getVerticesData) {
                const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
                if (positions) {
                    totalVertices += positions.length / 3;
                }
            }
            
            if (mesh.getIndices) {
                const indices = mesh.getIndices();
                if (indices) {
                    totalTriangles += indices.length / 3;
                }
            }
            
            if (mesh.material) {
                materials.add(mesh.material.name || 'UnnamedMaterial');
            }
        });

        // Get file size (if available from metadata)
        const fileSize = this._getFileSize(objectName);
        
        // Get GLTF extensions (placeholder for now)
        const gltfExtensions = this._getGLTFExtensions(objectName);

        return {
            defaultDimensions: dimensions,
            vertexCount: Math.round(totalVertices),
            triangleCount: Math.round(totalTriangles),
            materialCount: materials.size,
            fileSize: fileSize,
            gltfExtensions: gltfExtensions,
            boundingBox: boundingBox,
            meshCount: meshes.length
        };
    }

    /** Get all meshes from a transform node */
    private _getAllMeshes(transform: TransformNode): Mesh[] {
        const meshes: Mesh[] = [];
        
        // Add root if it's a mesh
        if (transform instanceof Mesh) {
            meshes.push(transform);
        }
        
        // Add all child meshes
        const childMeshes = transform.getChildMeshes ? transform.getChildMeshes(false) : [];
        childMeshes.forEach(mesh => {
            if (mesh instanceof Mesh) {
                meshes.push(mesh);
            }
        });
        
        return meshes;
    }

    /** Calculate bounding box for a collection of meshes */
    private _calculateBoundingBox(meshes: Mesh[]): { min: Vector3, max: Vector3 } {
        if (meshes.length === 0) {
            return { min: new Vector3(0, 0, 0), max: new Vector3(0, 0, 0) };
        }

        let minX = Infinity, minY = Infinity, minZ = Infinity;
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

        meshes.forEach(mesh => {
            const boundingInfo = mesh.getBoundingInfo();
            const min = boundingInfo.minimum;
            const max = boundingInfo.maximum;
            
            minX = Math.min(minX, min.x);
            minY = Math.min(minY, min.y);
            minZ = Math.min(minZ, min.z);
            maxX = Math.max(maxX, max.x);
            maxY = Math.max(maxY, max.y);
            maxZ = Math.max(maxZ, max.z);
        });

        return {
            min: new Vector3(minX, minY, minZ),
            max: new Vector3(maxX, maxY, maxZ)
        };
    }

    /** Get file size for an object */
    private _getFileSize(objectName: string): string {
        const sceneObject = this.traceScene.resolveSceneObject(objectName);
        if (!sceneObject) return "Unknown";

        // Try to get file size from the scene object's source
        const src = sceneObject.src;
        if (src) {
            // Check if we have cached file size
            const cached = this._infoCache.get(objectName);
            if (cached?.fileSize) {
                const size = this._formatFileSize(cached.fileSize);
                return cached.isEstimated ? `${size} (estimated)` : size;
            }

            // Try to fetch actual file size from URL first
            this._fetchFileSize(src).then(size => {
                if (size > 0) {
                    // Cache the file size
                    this._infoCache.set(objectName, { ...this._infoCache.get(objectName), fileSize: size });
                    Logger.log(`[ModelInfo] Cached actual file size for ${objectName}: ${this._formatFileSize(size)}`);
                } else {
                    // Fallback to estimation if HEAD request fails
                    this._fallbackToEstimation(objectName, sceneObject);
                }
            }).catch(() => {
                // Fallback to estimation if HEAD request fails
                this._fallbackToEstimation(objectName, sceneObject);
            });

            // Return "Loading..." while we fetch the actual size
            return "Loading...";
        }

        return "Unknown";
    }

    /** Fallback to estimation when actual file size can't be fetched */
    private _fallbackToEstimation(objectName: string, sceneObject: SceneObject): void {
        const src = sceneObject.src;
        if (src && src.toLowerCase().endsWith('.glb')) {
            const meshes = this._getAllMeshes(sceneObject.transform);
            const estimatedSize = this._estimateGLBSize(meshes);
            if (estimatedSize > 0) {
                this._infoCache.set(objectName, {
                    ...this._infoCache.get(objectName),
                    fileSize: estimatedSize,
                    isEstimated: true
                });
                Logger.log(`[ModelInfo] Using estimated file size for ${objectName}: ${this._formatFileSize(estimatedSize)}`);
            }
        }
    }

    /** Fetch actual file size from URL */
    private async _fetchFileSize(url: string): Promise<number> {
        try {
            // Try HEAD request first (most efficient)
            const headResponse = await fetch(url, { 
                method: 'HEAD',
                mode: 'cors'
            });
            
            if (headResponse.ok) {
                const contentLength = headResponse.headers.get('content-length');
                if (contentLength) {
                    return parseInt(contentLength, 10);
                }
            }
            
            // If HEAD fails, try GET with range request (less efficient but works with CORS)
            const rangeResponse = await fetch(url, {
                method: 'GET',
                headers: { 'Range': 'bytes=0-1' },
                mode: 'cors'
            });
            
            if (rangeResponse.ok) {
                const contentRange = rangeResponse.headers.get('content-range');
                if (contentRange) {
                    // Parse "bytes 0-1/12345" format
                    const match = contentRange.match(/\/(\d+)$/);
                    if (match) {
                        return parseInt(match[1], 10);
                    }
                }
            }
            
        } catch (error) {
            Logger.warn(`[ModelInfo] Failed to fetch file size for ${url}:`, error);
        }
        return 0;
    }

    /** Get GLTF extensions for an object */
    private _getGLTFExtensions(objectName: string): string[] {
        const sceneObject = this.traceScene.resolveSceneObject(objectName);
        if (!sceneObject) return [];

        // Check local cache for GLTF extensions
        const cached = this._infoCache.get(objectName);
        if (cached?.gltfExtensions) {
            return cached.gltfExtensions;
        }

        // Read from SceneObject (captured via onParsed during ImportMeshAsync)
        if (sceneObject.extensionsUsed) {
            this._infoCache.set(objectName, {
                ...this._infoCache.get(objectName),
                gltfExtensions: sceneObject.extensionsUsed
            });
            return sceneObject.extensionsUsed;
        }

        return [];
    }

    /** Format file size in human readable format */
    private _formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /** Estimate GLB file size based on mesh data */
    private _estimateGLBSize(meshes: Mesh[]): number {
        let totalSize = 0;
        
        meshes.forEach(mesh => {
            // Estimate vertex data size
            const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
            if (positions) {
                totalSize += positions.length * 4; // 4 bytes per float
            }
            
            // Estimate index data size
            const indices = mesh.getIndices();
            if (indices) {
                totalSize += indices.length * 2; // 2 bytes per index
            }
            
            // Estimate material data size (rough approximation)
            if (mesh.material) {
                totalSize += 1024; // Rough estimate for material data
            }
        });
        
        // Add GLB container overhead (headers, etc.)
        totalSize += 1024;
        
        return totalSize;
    }

    /** Get scene information - returns different data based on file type (GLB vs trace) */
    public getSceneInfo(): SceneInfoResult | null {
        const sceneObjects = this.traceScene.getSceneObjects();

        // Determine file type - if there's only one object and it's from a direct GLB/media file
        const isGLB = sceneObjects.length === 1 && this._isDirectMediaFile(sceneObjects[0]);
        
        if (isGLB) {
            // Return GLB model info for the single object
            const objectName = sceneObjects[0].name;
            const modelInfo = this.getModelInfo(objectName);
            return {
                fileType: 'glb',
                data: modelInfo
            };
        } else {
            // Return trace file statistics
            return {
                fileType: 'trace',
                data: this._getTraceFileStats(sceneObjects)
            };
        }
    }

    /** Check if a scene object is from a direct media file (not from a trace) */
    private _isDirectMediaFile(sceneObject: SceneObject): boolean {
        const src = sceneObject.src || '';
        const srcLower = src.toLowerCase();
        return srcLower.endsWith('.glb') || 
               srcLower.match(/\.(jpg|jpeg|png|mp4|webm|ogg|mov|avi)$/) !== null;
    }

    /** Get statistics for a trace file */
    private _getTraceFileStats(sceneObjects: SceneObject[]): TraceFileStats {
        let glbCount = 0;
        let particleSystemCount = 0;
        let imageCount = 0;
        let videoCount = 0;

        sceneObjects.forEach(obj => {
            const src = obj.src || '';
            const srcLower = src.toLowerCase();
            
            // Count GLB models
            if (srcLower.endsWith('.glb')) {
                glbCount++;
            }
            
            // Count images
            if (srcLower.match(/\.(png|jpg|jpeg|gif|webp|bmp|svg)$/)) {
                imageCount++;
            }
            
            // Count videos
            if (srcLower.match(/\.(mp4|webm|ogg|mov|avi)$/)) {
                videoCount++;
            }
            
            // Count particle systems
            const behaviors = obj.getSceneObjectData()?.behaviors;
            if (behaviors && Array.isArray(behaviors)) {
                const hasParticleSystem = behaviors.some((comp: BehaviorData) => comp['@type'] === 'ParticleSystem');
                if (hasParticleSystem) {
                    particleSystemCount++;
                }
            }
        });

        return {
            totalObjects: sceneObjects.length,
            glbCount,
            particleSystemCount,
            imageCount,
            videoCount
        };
    }
}
